import Notify from './notifyActions'
import {
  GET_SUBSCRIPTION_TIERS,
  CREATE_SUBSCRIPTION_TIER,
  UPDATE_SUBSCRIPTION_TIER,
  DELETE_SUBSCRIPTION_TIER,
  GET_TIER_FEATURES,
  UPDATE_TIER_FEATURES,
  UPDATE_TIER_FEATURES_SUCCESS,
  GET_TIER_PRICING,
  UPDATE_TIER_PRICING,
  GET_CHECKOUT_COUNTRIES,
  CREATE_CHECKOUT_COUNTRY,
  UPDATE_CHECKOUT_COUNTRY,
  DELETE_CHECKOUT_COUNTRY,
  UPDATE_COUNTRY_CURRENCIES,
  GET_TERMS_VERSIONS,
  CREATE_TERMS_VERSION,
  UPDATE_TERMS_VERSION,
  PUBLISH_TERMS_VERSION,
  DELETE_TERMS_VERSION,
  GET_TERMS_VERSION,
  SET_ACTIVE_TAB,
  SET_SELECTED_TIER,
  CLEAR_SUBMGMT_MESSAGES
} from '../reducers/subscriptionManagementReducer'
import type { ReduxAxiosAction, ReduxAction, AppDispatch, ThunkResult } from './common'

const API_BASE = 'subscription-management'
const FEATURES_UPDATED_TOAST = 'features_updated'
const FEATURES_UPDATE_FAILED_TOAST = 'features_update_failed'

// --- Tiers ---

export const getSubscriptionTiers = (): ReduxAxiosAction => ({
  type: GET_SUBSCRIPTION_TIERS,
  payload: {
    request: {
      mode: 'no-cors',
      url: `${API_BASE}/tiers`,
      method: 'GET'
    }
  }
})

export const createSubscriptionTier = (tierData: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: CREATE_SUBSCRIPTION_TIER,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/tiers`,
          method: 'POST',
          data: tierData
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Subscription tier created'))
      dispatch(getSubscriptionTiers())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to create subscription tier'))
    })
  }
}

export const updateSubscriptionTier = (tierId: number, tierData: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: UPDATE_SUBSCRIPTION_TIER,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/tiers/${tierId}`,
          method: 'PUT',
          data: tierData
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Subscription tier updated'))
      dispatch(getSubscriptionTiers())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to update subscription tier'))
    })
  }
}

export const deleteSubscriptionTier = (tierId: number): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: DELETE_SUBSCRIPTION_TIER,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/tiers/${tierId}`,
          method: 'DELETE'
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Subscription tier deleted'))
      dispatch(getSubscriptionTiers())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to delete subscription tier'))
    })
  }
}

// --- Tier Features ---

export const getTierFeatures = (tierId: number): ReduxAxiosAction => ({
  type: GET_TIER_FEATURES,
  payload: {
    request: {
      mode: 'no-cors',
      url: `${API_BASE}/tiers/${tierId}/features`,
      method: 'GET'
    }
  }
})

export const updateTierFeatures = (tierId: number, features: any[]): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: UPDATE_TIER_FEATURES,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/tiers/${tierId}/features`,
          method: 'PUT',
          data: features
        }
      }
    }).then((response: any) => {
      if (response.type !== UPDATE_TIER_FEATURES_SUCCESS) {
        dispatch(Notify.error(FEATURES_UPDATE_FAILED_TOAST))
        return Promise.reject(response)
      }

      dispatch(Notify.success(FEATURES_UPDATED_TOAST))
      dispatch(getTierFeatures(tierId))
      return response
    }, (error: any) => {
      dispatch(Notify.error(FEATURES_UPDATE_FAILED_TOAST))
      return Promise.reject(error)
    })
  }
}

// --- Tier Pricing ---

export const getTierPricing = (tierId: number): ReduxAxiosAction => ({
  type: GET_TIER_PRICING,
  payload: {
    request: {
      mode: 'no-cors',
      url: `${API_BASE}/tiers/${tierId}/pricing`,
      method: 'GET'
    }
  }
})

export const updateTierPricing = (tierId: number, pricing: any[]): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: UPDATE_TIER_PRICING,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/tiers/${tierId}/pricing`,
          method: 'PUT',
          data: pricing
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Tier pricing updated'))
      dispatch(getTierPricing(tierId))
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to update tier pricing'))
    })
  }
}

// --- Countries ---

export const getCheckoutCountries = (): ReduxAxiosAction => ({
  type: GET_CHECKOUT_COUNTRIES,
  payload: {
    request: {
      mode: 'no-cors',
      url: `${API_BASE}/countries`,
      method: 'GET'
    }
  }
})

export const createCheckoutCountry = (countryData: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: CREATE_CHECKOUT_COUNTRY,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/countries`,
          method: 'POST',
          data: countryData
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Country created'))
      dispatch(getCheckoutCountries())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to create country'))
    })
  }
}

export const updateCheckoutCountry = (countryId: number, countryData: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: UPDATE_CHECKOUT_COUNTRY,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/countries/${countryId}`,
          method: 'PUT',
          data: countryData
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Country updated'))
      dispatch(getCheckoutCountries())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to update country'))
    })
  }
}

export const deleteCheckoutCountry = (countryId: number): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: DELETE_CHECKOUT_COUNTRY,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/countries/${countryId}`,
          method: 'DELETE'
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Country deleted'))
      dispatch(getCheckoutCountries())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to delete country'))
    })
  }
}

// --- Country Currencies ---

export const updateCountryCurrencies = (countryId: number, currencies: any[]): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: UPDATE_COUNTRY_CURRENCIES,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/countries/${countryId}/currencies`,
          method: 'PUT',
          data: currencies
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Country currencies updated'))
      dispatch(getCheckoutCountries())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to update country currencies'))
    })
  }
}

// --- Local state ---

export const setActiveTab = (tab: 'tiers' | 'countries' | 'terms'): ReduxAction<string> => ({
  type: SET_ACTIVE_TAB,
  payload: tab
})

export const setSelectedTier = (tierId: number | null): ReduxAction<number | null> => ({
  type: SET_SELECTED_TIER,
  payload: tierId
})

export const clearMessages = (): ReduxAction => ({
  type: CLEAR_SUBMGMT_MESSAGES
})

// --- Terms & Conditions ---

export const getTermsVersions = (): ReduxAxiosAction => ({
  type: GET_TERMS_VERSIONS,
  payload: {
    request: {
      mode: 'no-cors',
      url: `${API_BASE}/terms`,
      method: 'GET'
    }
  }
})

export const createTermsVersion = (termsData: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: CREATE_TERMS_VERSION,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/terms`,
          method: 'POST',
          data: termsData
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Terms version created'))
      dispatch(getTermsVersions())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to create terms version'))
    })
  }
}

export const updateTermsVersion = (termsId: number, termsData: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: UPDATE_TERMS_VERSION,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/terms/${termsId}`,
          method: 'PUT',
          data: termsData
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Terms version updated'))
      dispatch(getTermsVersions())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to update terms version'))
    })
  }
}

export const publishTermsVersion = (termsId: number): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: PUBLISH_TERMS_VERSION,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/terms/${termsId}/publish`,
          method: 'POST'
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Terms version published'))
      dispatch(getTermsVersions())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to publish terms version'))
    })
  }
}

export const deleteTermsVersion = (termsId: number): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: DELETE_TERMS_VERSION,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/terms/${termsId}`,
          method: 'DELETE'
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Terms version deleted'))
      dispatch(getTermsVersions())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to delete terms version'))
    })
  }
}

export const getTermsVersion = (termsId: number): ReduxAxiosAction => ({
  type: GET_TERMS_VERSION,
  payload: {
    request: {
      mode: 'no-cors',
      url: `${API_BASE}/terms/${termsId}`,
      method: 'GET'
    }
  }
})

export const duplicateTermsVersion = (termsId: number): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return dispatch({
      type: CREATE_TERMS_VERSION,
      payload: {
        request: {
          mode: 'no-cors',
          url: `${API_BASE}/terms/${termsId}/duplicate`,
          method: 'POST'
        }
      }
    }).then((response: any) => {
      dispatch(Notify.success('Terms version duplicated'))
      dispatch(getTermsVersions())
      return response
    }).catch(() => {
      dispatch(Notify.error('Failed to duplicate terms version'))
    })
  }
}
